import React, { Component } from 'react';
import PropTypes from 'prop-types';
import onClickOutside from 'react-onclickoutside';
import axios from '../lib/axios';

import AutocompleteInput from './AutocompleteInput';
import MentionsPicker from './MentionsPicker';
import qs from "qs";

const KEY = {
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  UP: 38,
  DOWN: 40,
};

export class MultiEntitySelectInput extends Component {
  static propTypes = {
    hideEmpty: PropTypes.bool,
    apiUrls: PropTypes.arrayOf(
      PropTypes.string,
    ).isRequired,
    open: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      })),
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ]),
    multiple: PropTypes.bool,
    translationsApiUrls: PropTypes.object,
    customOptions: PropTypes.array,
    showCustomOptionsOnBlank: PropTypes.bool,
    requestThrottle: PropTypes.number,
  }

  static defaultProps = {
    hideEmpty: true,
    defaultValue: [],
    results: [],
    open: false,
    multiple: true,
    resultsAs: 'tags',
    translationsApiUrls: {},
    showCustomOptionsOnBlank: false,
    customOptions: [{
      key: 'new-company',
      title: ' New company',
      subtitle: 'Add company to the database',
      avatar: ' ', // TODO Find a better way to hide the avatar
      type: 'company',
    }],
    requestThrottle: 250,
  }

  constructor(props) {
    super(props);

    this.requestCancelToken = axios.CancelToken.source();

    const defaultValue = Array.isArray(props.defaultValue) ?
      this.buildSelected(this.responseToProps(props.defaultValue)) :
      this.buildSelected(this.responseToProps([props.defaultValue]));

    this.state = {
      open: props.open,
      loadingSelected: false,
      selected: defaultValue,
      selectedIndex: 0,
      results: [],
    };
  }

  shiftSelectedIndex = (delta) => {
    const count = this.state.results.length;
    this.setState({
      selectedIndex: (count + this.state.selectedIndex + delta) % count,
    });
  };

  // Map the API response to the formats used by the components
  // TODO Standardize the format!
  responseToProps(json) {
    return json.map((item) => {
      let subtitle;
      if (item.object_type === 'user') {
        subtitle = "Individual"
      } else if (item.object_type === 'investor') {
        subtitle = 'Individual Investor';
      } else if (item.object_type === 'role') {
        subtitle = `Role (${item.users_count} person${item.users_count !== 1 ? 's' : ''})`
      } else if (item.object_type === 'company') {
        subtitle = `Company (${item.users_count} person${item.users_count !== 1 ? 's' : ''})`
      } else if (item.object_type === 'investment_firm') {
        subtitle = `Investment Firm (${item.users_count} person${item.users_count !== 1 ? 's' : ''})`
      } else if (item.object_type === 'group') {
        subtitle = `Group (${item.users_count} person${item.users_count !== 1 ? 's' : ''})`
      }

      return {
        // Used by the AutocompleteInput
        id: item.object_type === 'group' ? item.slug : item.id,
        name: item.name,
        company_name: item.company_name,
        roles: item.preloadable_roles_sentence,
        // Used by the MentionsPicker
        key: item.id,
        title: item.name,
        subtitle: subtitle,
        avatar: item.avatar_url,
        type: item.object_type,
        users_count: item.users_count
      };
    });
  }

  handleInputClick = () => {
    return this.setState({
      results: this.props.showCustomOptionsOnBlank ? this.props.customOptions : undefined,
      open: true
    });
  }

  handleInputChange = (e) => {
    if (this.requests) {
      this.requestCancelToken.cancel();
    }

    const query = e.target.value;

    if (!query) {
      return this.setState({ results: [], open: false });
    }

    clearTimeout(this.requestThrottle);
    this.requestThrottle = setTimeout(
      () => this.performRequests(this.props.apiUrls, { query }),
      this.props.requestThrottle
    );
  }

  sortByName(list) {
    return list.sort(
      (a, b) => {
        return a.name.localeCompare(b.name);
      });
  }

  buildSelected(list) {
    const sortedList = this.sortByName(list);
    return sortedList.reduce((acc, item) => {
      if (acc.findIndex(i => i.id === item.id) === -1) {
        acc.push(Object.assign({}, item, { index: acc.length + 1 }));
      }
      return acc;
    }, [])
  }

  performRequests = (urls, params) => {
    this.requestCancelToken = axios.CancelToken.source();
    this.requests = this.props.apiUrls.map((url) => axios.get(url, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets', depth: 2 }),
      cancelToken: this.requestCancelToken.token
    }));

    axios.all(this.requests)
      .then((...args) => {
        const response = [].concat(args[0].flatMap((r) => r.data));
        const results = this.sortByName(this.responseToProps(response))
          .filter((r) => {
            return this.props.hideEmpty ? r.users_count !== 0 : true;
          });

        if (results.length) {
          results.push(...this.props.customOptions);
        }

        this.setState({ results, open: true });
      });
  };

  handleResultClick = (item) => {
    // If the user picks one of the placeholder options, just close the dropdown
    if (!item.id) return this.setState({ open: false });

    if (this.state.selected.filter((t) => t.id === item.id).length === 0) {
      if (this.props.translationsApiUrls.hasOwnProperty(item.type)) {
        // fetch company users and fill them into the results.
        this.setState({ open: false, selectedIndex: 0, loadingSelected: true });
        axios.get(this.props.translationsApiUrls[item.type].replace(`__${item.type.toUpperCase()}__`, item.id))
          .then((response) => {
            const selected = this.buildSelected(this.state.selected.concat(this.responseToProps(response.data)));
            this.setState({
              selected,
              loadingSelected: false
            });
          });
      } else {
        const selected = this.buildSelected(this.state.selected.concat([item]));
        this.setState({
          selected,
          open: false,
          selectedIndex: 0,
        });
      }
    }
  }

  handleClearAll = () => {
    this.setState({ selected: [] });
  }

  handleSelectedClick = (item) => {
    const index = this.state.selected.findIndex(e => e.id === item.id);
    const selected = this.state.selected.slice(0, index)
      .concat(this.state.selected.slice(index + 1)); // Remove without mutating

    this.setState({ selected: this.buildSelected(selected) });
  }

  handleMentionMouseEnter = (selectedIndex) => {
    this.setState({ selectedIndex });
  }

  handleClickOutside = () => {
    if (this.state.open) {
      this.setState({ open: false });
    }
  }

  handleKeyDown = (e) => {
    if (!this.state.open) return;

    switch (e.keyCode) {
      case KEY.ESC:
        this.setState({ open: false });
        break;
      case KEY.UP:
        e.preventDefault();
        this.shiftSelectedIndex(-1);
        break;
      case KEY.DOWN:
        e.preventDefault();
        this.shiftSelectedIndex(+1);
        break;
      case KEY.TAB:
      case KEY.RETURN: {
        e.preventDefault();
        const option = this.state.results[this.state.selectedIndex];
        this.handleResultClick(option);
        break;
      }
      default:
    }
  }

  render() {
    return (
      <AutocompleteInput
        onChange={this.handleInputChange}
        onKeyDown={this.handleKeyDown}
        onClick={this.handleInputClick}
        onClickSelected={this.handleSelectedClick}
        onClearAll={this.handleClearAll}
        selected={this.state.selected}
        loadingSelected={this.state.loadingSelected}
        {...this.props}
      >
        { this.state.open &&
          <MentionsPicker
            options={this.state.results}
            onClick={this.handleResultClick}
            onMouseEnter={this.handleMentionMouseEnter}
            selected={this.state.selectedIndex}
          />
        }
      </AutocompleteInput>
    );
  }
}

export default onClickOutside(MultiEntitySelectInput);
